import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPages, fetchGalleries } from "../lib/cms";
import { clinicInfo } from "../data";

export default function Home() {
  const [pageData, setPageData] = useState(null);
  const [galleryImages, setGalleryImages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        const pages = await fetchPages();
        const forside = pages.find(
          (p) => p.slug?.da === "forside" || p.slug === "forside"
        );
        setPageData(forside);

        const galleries = await fetchGalleries();
        const klinikGallery = galleries.find(
          (g) => g.slug?.da === "klinikken" || g.slug === "klinikken"
        );
        if (klinikGallery && klinikGallery.images) {
          setGalleryImages(klinikGallery.images);
        }
      } catch (error) {
        console.error("Error loading home page data:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-4 border-brand-200 border-t-brand-600" />
      </div>
    );
  }

  // Fallback image URLs
  const heroImg = "https://assets.auxodev.dk/cdn-cgi/image/width=1200,quality=85,format=auto/assets/783a3379-7262-480b-b55e-1bf6fb783275/kiropraktor-assens.jpg";
  const jesImg = "https://assets.auxodev.dk/cdn-cgi/image/width=600,quality=85,format=auto/assets/783a3379-7262-480b-b55e-1bf6fb783275/foto-jes.jpeg";
  const treatImg = "https://assets.auxodev.dk/cdn-cgi/image/width=800,quality=85,format=auto/assets/783a3379-7262-480b-b55e-1bf6fb783275/kiropraktor-assens-ondt-i-ryggen.jpg";

  return (
    <div className="space-y-24 pb-24">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-100/50 via-brand-50/30 to-white py-16 lg:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left Content */}
            <div className="lg:col-span-6 space-y-8 text-left">
              <div className="inline-flex items-center gap-2 rounded-full bg-brand-100 px-4 py-1.5 text-xs font-semibold text-brand-800 tracking-wide uppercase">
                <span className="h-2 w-2 rounded-full bg-brand-500 animate-pulse" />
                Din lokale kiropraktor på Vestfyn
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-brand-950 leading-tight">
                Din kiropraktor <br />
                <span className="text-brand-600 italic font-normal">på Vestfyn</span>
              </h1>
              <p className="text-lg sm:text-xl text-brand-800 font-medium max-w-xl">
                Hos os er du altid i gode hænder! Vi tilbyder grundig, tryg og professionel behandling tilpasset dig.
              </p>
              <div className="flex flex-wrap gap-4 pt-2">
                <a
                  href={clinicInfo.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center rounded-full bg-brand-600 px-8 py-3.5 text-base font-semibold text-white shadow-md hover:bg-brand-700 transition-all duration-200 hover:scale-102 active:scale-98 cursor-pointer"
                >
                  Bestil tid online
                </a>
                <Link
                  to="/behandlinger"
                  className="inline-flex items-center justify-center rounded-full bg-white border-2 border-brand-200 px-8 py-3.5 text-base font-semibold text-brand-800 hover:bg-brand-50 hover:border-brand-300 transition-all duration-200 cursor-pointer"
                >
                  Se behandlinger
                </Link>
              </div>
            </div>

            {/* Right Image */}
            <div className="lg:col-span-6 relative">
              <div className="absolute -inset-4 rounded-3xl bg-brand-200/40 blur-2xl -z-10" />
              <div className="overflow-hidden rounded-3xl border border-brand-100 shadow-xl">
                <img
                  src={heroImg}
                  alt="Kiropraktisk Klinik Assens"
                  className="w-full h-[450px] object-cover object-center"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NYHED! Section */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="overflow-hidden rounded-3xl bg-brand-900 text-white shadow-xl">
          <div className="grid grid-cols-1 lg:grid-cols-12">
            {/* Left Image */}
            <div className="lg:col-span-5 h-[350px] lg:h-auto relative">
              <img
                src={jesImg}
                alt="Johannes Smedegaard"
                className="absolute inset-0 w-full h-full object-cover object-center"
              />
            </div>

            {/* Right Content */}
            <div className="lg:col-span-7 p-8 sm:p-12 lg:p-16 flex flex-col justify-center text-left space-y-6">
              <div className="inline-flex self-start rounded-full bg-brand-800 px-3.5 py-1 text-xs font-semibold tracking-wider uppercase text-brand-200">
                Efterårsferie hos Kiropraktisk Klinik Assens
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-white font-display">
                Ondt i ryggen holder ikke efterårsferie – det gør vi heller ikke
              </h2>
              <div className="text-base text-brand-200 space-y-4 font-normal">
                <p className="text-lg font-medium text-white leading-relaxed">
                  Klinikken holder åbent som normalt i efterårsferien, så du fortsat kan få hjælp, hvis ryggen, nakken eller kroppen driller efter havearbejde, sport, lange gåture eller en aktiv ferie.
                </p>
                <p className="text-brand-100 leading-relaxed">
                  Vi er på klinikken gennem hele efterårsferien og står klar til at hjælpe både nye og eksisterende patienter.
                </p>
                <div className="pt-2">
                  <p className="font-semibold text-white text-lg font-display">
                    Normale åbnings- og telefontider
                  </p>
                  <p className="text-brand-200 mt-1">
                    I efterårsferien har klinikken <strong className="text-white font-semibold">åbent som normalt</strong>, og vores <strong className="text-white font-semibold">telefontider er også uændrede</strong>.
                  </p>
                </div>
                <p className="text-brand-100 pt-2 border-t border-brand-800/80">
                  Vi ønsker alle en rigtig god efterårsferie og vi glæder os til at hjælpe jer på klinikken.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Core Values (Tryghed, Faglighed, Nærvær) */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center space-y-12">
        <div className="max-w-2xl mx-auto space-y-4">
          <h2 className="text-3xl sm:text-4xl font-bold font-display text-brand-950">
            Vores kerneværdier
          </h2>
          <p className="text-brand-700">
            Vi tror på, at en effektiv kiropraktorbehandling er med til at skabe både trivsel og balance i din hverdag.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white p-8 rounded-2xl border border-brand-100 shadow-xs space-y-4 text-left">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
              <i className="fa-light fa-heart-pulse text-2xl" />
            </div>
            <h3 className="text-xl font-bold font-display text-brand-900">Tryghed</h3>
            <p className="text-sm text-brand-700 leading-relaxed">
              Vi forstår, at kiropraktisk behandling kan virke grænseoverskridende, især hvis du aldrig har prøvet det før. Vi tager hensyn til dine behov, og det behøver ikke at sige knæk.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-brand-100 shadow-xs space-y-4 text-left">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
              <i className="fa-light fa-graduation-cap text-2xl" />
            </div>
            <h3 className="text-xl font-bold font-display text-brand-900">Faglighed</h3>
            <p className="text-sm text-brand-700 leading-relaxed">
              Vores kiropraktorer er autoriserede og har 6 års uddannelse fra Syddansk Universitet. Vi implementerer den nyeste viden og efteruddanner os løbende for at sikre den bedste behandling.
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl border border-brand-100 shadow-xs space-y-4 text-left">
            <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
              <i className="fa-light fa-handshake-angle text-2xl" />
            </div>
            <h3 className="text-xl font-bold font-display text-brand-900">Nærvær</h3>
            <p className="text-sm text-brand-700 leading-relaxed">
              Vi tager os god tid til at lytte, diagnosticere, behandle og forebygge. Vi tilpasser altid behandlingsforløbet til lige netop din problemstilling, tilstand og funktion.
            </p>
          </div>
        </div>
      </section>

      {/* Quick Treatment Links */}
      <section className="bg-brand-100/50 py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 space-y-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div className="space-y-4 text-left">
              <h2 className="text-3xl sm:text-4xl font-bold font-display text-brand-950">
                Hvad kan vi hjælpe dig med?
              </h2>
              <p className="text-brand-700 max-w-xl">
                Vores kiropraktorer er specialiserede i at behandle muskler, led og sener i hele kroppen – ikke kun rygsøjlen.
              </p>
            </div>
            <Link
              to="/behandlinger"
              className="inline-flex items-center justify-center rounded-full bg-brand-600 px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-brand-700 transition-colors cursor-pointer"
            >
              Se alle behandlinger
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              {
                title: "Ryg",
                icon: "fa-light fa-spine",
                desc: "Behandling af lænd, brystryg, hold i ryggen, diskusprolaps og spinalstenose.",
                href: "/behandlinger/ryg",
              },
              {
                title: "Arme og ben",
                icon: "fa-light fa-joint",
                desc: "Hjælp til tennisalbue, knæproblemer, forstuvninger, hælspore og sportsskader.",
                href: "/behandlinger/arme-og-ben",
              },
              {
                title: "Skulder",
                icon: "fa-light fa-child-reaching",
                desc: "Behandling af indeklemning, senebetændelse, frossen skulder og nedsat bevægelighed.",
                href: "/behandlinger/skulder",
              },
              {
                title: "Hofte",
                icon: "fa-light fa-person-walking",
                desc: "Hjælp til hoftesmerter, slimsæksbetændelse, slidgigt og bækkenproblemer.",
                href: "/behandlinger/hofte",
              },
            ].map((item) => (
              <Link
                key={item.title}
                to={item.href}
                className="group bg-white p-6 rounded-2xl border border-brand-100 shadow-xs hover:shadow-md hover:border-brand-200 transition-all duration-200 text-left flex flex-col justify-between h-full cursor-pointer"
              >
                <div className="space-y-4">
                  <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-600 transition-colors duration-200 group-hover:bg-brand-600 group-hover:text-white">
                    <i className={`${item.icon} text-2xl`} />
                  </div>
                  <h3 className="text-lg font-bold font-display text-brand-900 group-hover:text-brand-600 transition-colors">
                    {item.title}
                  </h3>
                  <p className="text-xs text-brand-700 leading-relaxed">{item.desc}</p>
                </div>
                <div className="pt-6 flex items-center gap-1.5 text-xs font-semibold text-brand-600 group-hover:text-brand-800 transition-colors">
                  Læs mere
                  <i className="fa-solid fa-chevron-right text-[10px] transition-transform duration-200 group-hover:translate-x-0.5" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Patient Information (No referral needed) */}
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left Image */}
          <div className="lg:col-span-5 overflow-hidden rounded-3xl border border-brand-100 shadow-lg">
            <img
              src={treatImg}
              alt="Kiropraktisk behandling"
              className="w-full h-[400px] object-cover object-center"
            />
          </div>

          {/* Right Content */}
          <div className="lg:col-span-7 text-left space-y-6">
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-brand-950">
              Du behøver ikke en henvisning for at komme til kiropraktor
            </h2>
            <p className="text-brand-700 leading-relaxed">
              Klinikken har overenskomst med sygesikringen (ydernummer), så du får automatisk tilskud uden lægehenvisning. Vi behandler folk i alle aldre og livsfaser.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pt-4">
              <Link to="/info/gravide" className="flex gap-4 p-4 rounded-xl hover:bg-brand-100/30 transition-colors cursor-pointer">
                <div className="text-brand-600"><i className="fa-light fa-person-pregnant text-2xl" /></div>
                <div>
                  <h4 className="font-bold text-brand-900">Gravide</h4>
                  <p className="text-xs text-brand-700 mt-1">Lindre smerter i bækken, lænd og skuldre under graviditeten.</p>
                </div>
              </Link>
              <Link to="/info/babyer" className="flex gap-4 p-4 rounded-xl hover:bg-brand-100/30 transition-colors cursor-pointer">
                <div className="text-brand-600"><i className="fa-light fa-baby text-2xl" /></div>
                <div>
                  <h4 className="font-bold text-brand-900">Babyer & Børn</h4>
                  <p className="text-xs text-brand-700 mt-1">Blid behandling til kolik, skævt baghoved og motoriske udfordringer.</p>
                </div>
              </Link>
              <Link to="/info/voksen-og-senior" className="flex gap-4 p-4 rounded-xl hover:bg-brand-100/30 transition-colors cursor-pointer">
                <div className="text-brand-600"><i className="fa-light fa-user text-2xl" /></div>
                <div>
                  <h4 className="font-bold text-brand-900">Voksne & Seniorer</h4>
                  <p className="text-xs text-brand-700 mt-1">Hjælp til rygproblemer, nakkesmerter, hovedpine og slidgigt.</p>
                </div>
              </Link>
              <Link to="/info/pakkeforloeb" className="flex gap-4 p-4 rounded-xl hover:bg-brand-100/30 transition-colors cursor-pointer">
                <div className="text-brand-600"><i className="fa-light fa-box-heart text-2xl" /></div>
                <div>
                  <h4 className="font-bold text-brand-900">Pakkeforløb</h4>
                  <p className="text-xs text-brand-700 mt-1">Større tilskud ved diskusprolaps og spinalstenose.</p>
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Clinic Gallery Preview */}
      {galleryImages.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center space-y-12">
          <div className="max-w-2xl mx-auto space-y-4">
            <h2 className="text-3xl sm:text-4xl font-bold font-display text-brand-950">
              Kig indenfor i klinikken
            </h2>
            <p className="text-brand-700">
              Vi holder til i lyse, behagelige og rolige behandlingsrum på 1. sal i det gamle sygehus i Assens.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {galleryImages.map((img, idx) => (
              <div
                key={idx}
                className="overflow-hidden rounded-2xl border border-brand-100 shadow-xs aspect-4/3 group"
              >
                <img
                  src={`${img}?width=600&quality=80&format=auto`}
                  alt={`Klinikbillede ${idx + 1}`}
                  className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
